/**
 * A reseller's DRAFT commission plan, edited on its behalf (TMMPDM-0041, TMMAPI-0104, MM-D-106):
 * `/partners/:resellerId/commission-plans/:planId/edit` — the 0036 reseller-card editor pattern.
 * The plan is read fresh; one that is no longer a Draft shows a clear state with Copy. Name; Mode
 * (Simple: one rate for N months or for life; Advanced: 1–6 phases whose From is read-only — 1, then
 * the previous To + 1 — and whose last To may be blank, "Onward"); Applies to; Cool-off; After the
 * affiliate ends. Switching to Simple is offered only with one phase (the server refuses otherwise
 * too). The preview panel prices one subscriber on a granted customer rate card that already has a published version, and sends that card's id. That filter lets the preview resolve a version; it is not a verdict that the reseller can sell. "Check"
 * and a debounced check 600 ms after the last edit run the server's dry run (stale answers are
 * dropped); its `valid`, its `fields` on their inputs (`phases[i].…` on row i) and its 24-month
 * example. The console never decides validity or compliance. Save PATCHes the whole body and
 * returns to the tab; a 400 lands on the fields and the error summary; a 409 says the plan is no
 * longer a draft, with Reload. A Fix this plan copy arrives with a banner naming the original.
 * TMMPDM-0046 (`useLivePreview`): no check runs until the reseller's cards have loaded ("Loading
 * rate cards…"; a failed load is its alert and no verdict); the Draft opens with its verdict;
 * inputs the client rejects clear the verdict and say which fields to fix; every edit drops the
 * verdict and any older answer ("Checking…" until the new one); the error summary is the last
 * Save's, less the fields edited since; a save that lands after the user left doesn't move them,
 * and the return is `replace`.
 * TMMPDM-0049: a Save's inline message stays until that field is edited or the next Save (a preview
 * answer never clears it, and Check sends nothing — and clears nothing — until a rate card is
 * chosen). No preview body is sent without `rateCardId`. The verdict is the server's `valid`,
 * never composed from `fields` keys.
 * TMMPDM-0043 (TMMAPI-0105): Advanced adds a bounty, up to five milestones and two optional caps.
 * Simple hides them. TMMPDM-0050: Simple is disabled while any of them is set (the server refuses
 * the switch). Client checks cover only the fields the current mode shows. A bounty or a cap of
 * 0 is an error; empty means none. Simple always sends the clears (`0` / `[]`). "Capped" is the
 * preview month's `capped`, never computed.
 *
 * `/partners/:resellerId/commission-plans/new` goes to the Commission plans tab, where New
 * commission plan creates the draft (a POST) and opens it here — nothing is created from a URL.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  ErrorState,
  FORM_MAX_WIDTH,
  FormField,
  FormPage,
  FormSection,
  PageHeader,
  SubmitButton,
  useNotify,
  type Crumb,
  type ErrorSummaryItem,
} from '@/components/page';
import { Amount, HelpPopover } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  catalogFields,
  commissionPlanNameTaken,
  errorCopy,
  isStaleWrite,
  isValidationFailed,
  validationFields,
} from '@/api/errors';
import type {
  CommissionAfterEnd,
  CommissionAppliesTo,
  CommissionPhase,
  CommissionPlanMode,
  CommissionPlanPreview,
  CommissionPlanPreviewExample,
  CommissionPlanPreviewRequest,
  CommissionPlanView,
  CommissionPlanWriteRequest,
  CustomerRateCardView,
  ResellerDto,
} from '@/api/tmm/types';
import { formatDate, formatMoney, formatPercentBp, majorFromMinor } from '@/format';
import { invalidMoney } from '../addons/addonFormat';
import { parseMajor } from '../rateCards/rateCardInputs';
import { commissionPlanEditPath, type FixedFromState } from './commissionPlanFormat';
import { useLivePreview } from './useLivePreview';
import { useAdoptedCardLimits } from './useAdoptedCardLimits';

/** ADVANCED: at most this many phases (TMMAPI-0104). */
const MAX_PHASES = 6;
/** ADVANCED: at most this many milestones (TMMAPI-0105). */
const MAX_MILESTONES = 5;
const MAX_BOUNTY_ON_PAYMENT = 12;
const NAME_MAX = 80;
const COOL_OFF_MAX = 365;

const NAME_WIDTH = '40ch';
const VALUE_WIDTH = '20ch';

/** One ADVANCED row as typed; its From is derived, never typed. */
interface PhaseRow {
  key: string;
  to: string;
  rate: string;
}

/** One milestone row as typed. Empty inputs are not sent. */
interface MilestoneRow {
  key: string;
  qualified: string;
  amount: string;
}

const PHASE_KEY = /^phases\[(\d+)\]\.(fromMonth|toMonth|rateBp)$/;
const MILESTONE_KEY = /^milestones\[(\d+)\]\.(qualifiedReferrals|amountMinor)$/;

function newMilestone(qualified = '', amount = ''): MilestoneRow {
  return { key: crypto.randomUUID(), qualified, amount };
}

/** `phases[1].rateBp` → `commission-plan-phases-1-rateBp`. */
const fieldId = (key: string) =>
  `commission-plan-${key.replace(/\[(\d+)\]\./, '-$1-').replace(/[^\w-]/g, '-')}`;

const affiliateCommissionsTab = (resellerId: number) =>
  `/partners/${resellerId}?tab=commissionPlans`;

function pct(bp: number): string {
  return String(bp / 100);
}

function newRow(to = '', rate = ''): PhaseRow {
  return { key: crypto.randomUUID(), to, rate };
}

function invalidRate(value: string): boolean {
  if (value.trim() === '') return true;
  const n = Number(value);
  return !Number.isFinite(n) || n < 0 || n > 100;
}

function invalidWhole(value: string, min: number, max: number): boolean {
  if (value.trim() === '') return true;
  const n = Number(value);
  return !Number.isInteger(n) || n < min || n > max;
}

/** Row i's From: 1, then the previous To + 1 — `null` while the previous To isn't a month. */
function fromOf(rows: PhaseRow[], index: number): number | null {
  if (index === 0) return 1;
  const previous = rows[index - 1];
  if (!previous || invalidWhole(previous.to, 1, Number.MAX_SAFE_INTEGER)) return null;
  return Number(previous.to) + 1;
}

function withoutKeys(
  messages: Record<string, string>,
  drop: (key: string) => boolean,
): Record<string, string> {
  const kept = Object.entries(messages).filter(([key]) => !drop(key));
  return kept.length === Object.keys(messages).length ? messages : Object.fromEntries(kept);
}

/** `/partners/:resellerId/commission-plans/new` → the Commission plans tab. */
export function CommissionPlanNewPage() {
  const { resellerId } = useParams();
  return <Navigate to={affiliateCommissionsTab(Number(resellerId))} replace />;
}

export function CommissionPlanEditPage() {
  const { resellerId, planId } = useParams();
  return (
    <CommissionPlanEditContent
      key={`${resellerId}-${planId}`}
      resellerId={Number(resellerId)}
      planId={Number(planId)}
    />
  );
}

function CommissionPlanEditContent({ resellerId, planId }: { resellerId: number; planId: number }) {
  const { t } = useCopy();
  const navigate = useNavigate();
  const notify = useNotify();
  const validIds =
    Number.isInteger(resellerId) && resellerId > 0 && Number.isInteger(planId) && planId > 0;
  const [loaded, setLoaded] = useState<{ reseller: ResellerDto; plan: CommissionPlanView } | null>(
    null,
  );
  const [loadFailure, setLoadFailure] = useState<unknown>(
    validIds ? null : new ApiError('UNKNOWN', 0),
  );
  const [reloadToken, setReloadToken] = useState(0);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    if (!validIds) return;
    let cancelled = false;
    Promise.all([adminApi.getReseller(resellerId), adminApi.getCommissionPlan(resellerId, planId)])
      .then(([reseller, plan]) => {
        if (cancelled) return;
        setLoaded({ reseller, plan });
        setLoadFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoaded(null);
        setLoadFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, planId, validIds, reloadToken]);

  const reload = () => {
    setLoaded(null);
    setLoadFailure(null);
    setReloadToken((n) => n + 1);
  };

  const resellersCrumb: Crumb = { label: t('page.partners.title', 'Partners'), to: '/partners' };
  const tabCrumb: Crumb = {
    label: t('partners.tab.commissionPlans', 'Commission plans'),
    to: affiliateCommissionsTab(resellerId),
  };

  // One header for every state of the page (TMM-B7): the loading page, the failure, the plan that is
  // not a Draft and the editor are the same `PageHeader` element, so the `h1` is one element whose
  // text changes — never replaced — and the late title is announced once.
  if (loadFailure !== null) {
    const title = t('commissionPlans.form.loadError', "Couldn't load this commission plan.");
    return (
      <Box>
        <PageHeader title={title} breadcrumbs={[resellersCrumb, tabCrumb, { label: title }]} />
        <ErrorState
          error={loadFailure}
          message={title}
          {...(validIds ? { onRetry: reload } : {})}
        />
      </Box>
    );
  }

  if (loaded === null) {
    const title = t('commissionPlans.form.loadingTitle', 'Commission plan');
    return (
      <Box>
        <PageHeader
          title={title}
          subtitle={t('common.loadingPage', 'Loading…')}
          breadcrumbs={[resellersCrumb, tabCrumb, { label: title }]}
        />
        <LoadingSpinner />
      </Box>
    );
  }

  const { reseller, plan } = loaded;
  const breadcrumbs: Crumb[] = [
    resellersCrumb,
    { label: reseller.name, to: `/partners/${resellerId}` },
    tabCrumb,
    { label: plan.name },
  ];
  const title = t('commissionPlans.form.title', 'Edit {{name}}', { name: plan.name });

  if (plan.status !== 'DRAFT') {
    const copy = async () => {
      setCopying(true);
      setCopyError(null);
      try {
        const next = await adminApi.copyCommissionPlan(resellerId, plan.id);
        notify.success(
          t('commissionPlans.toast.copied', '{{name}} is a new draft. Edit it, then activate it.', {
            name: next.name,
          }),
        );
        void navigate(commissionPlanEditPath(resellerId, next.id), { replace: true });
      } catch (err) {
        const fallback = t('commissionPlans.errors.copy', "Couldn't copy the commission plan.");
        const taken = commissionPlanNameTaken(err);
        setCopyError(
          taken === null
            ? errorCopy(err, t, fallback)
            : taken
              ? t(
                  'commissionPlans.errors.nameTaken',
                  'Another of this reseller\'s plans is called "{{name}}".',
                  { name: taken },
                )
              : fallback,
        );
      } finally {
        setCopying(false);
      }
    };
    return (
      <Box>
        <PageHeader title={title} breadcrumbs={breadcrumbs} />
        <Box sx={{ maxWidth: FORM_MAX_WIDTH }}>
          <ErrorState
            error={null}
            message={t(
              'commissionPlans.form.notDraft',
              'Only a draft plan can be edited. Copy it to make changes.',
            )}
            action={
              <SubmitButton
                variant="text"
                color="inherit"
                size="small"
                busy={copying}
                onClick={() => void copy()}
              >
                {t('rateCards.actions.copy', 'Copy')}
              </SubmitButton>
            }
          />
          {copyError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {copyError}
            </Alert>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={title} breadcrumbs={breadcrumbs} />
      <CommissionPlanEditor
        key={reloadToken}
        resellerId={resellerId}
        plan={plan}
        onReload={reload}
      />
    </Box>
  );
}

function CommissionPlanEditor({
  resellerId,
  plan,
  onReload,
}: {
  resellerId: number;
  plan: CommissionPlanView;
  onReload: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotify();
  const { backTo } = useReturnTo();
  const fixedFrom = (location.state as FixedFromState | null)?.fixedFrom;
  const first = plan.phases[0];

  const [name, setName] = useState(plan.name);
  const [mode, setMode] = useState<CommissionPlanMode>(plan.mode);
  const [simpleRate, setSimpleRate] = useState(first ? pct(first.rateBp) : '');
  const [simpleLife, setSimpleLife] = useState(first !== undefined && first.toMonth === undefined);
  const [simpleMonths, setSimpleMonths] = useState(
    first?.toMonth === undefined ? '12' : String(first.toMonth),
  );
  const [rows, setRows] = useState<PhaseRow[]>(() =>
    plan.phases.length > 0
      ? plan.phases.map((phase) =>
          newRow(phase.toMonth === undefined ? '' : String(phase.toMonth), pct(phase.rateBp)),
        )
      : [newRow()],
  );
  const [appliesTo, setAppliesTo] = useState<CommissionAppliesTo>(plan.appliesTo);
  const [coolOff, setCoolOff] = useState(String(plan.coolOffDays));
  const [afterEnd, setAfterEnd] = useState<CommissionAfterEnd>(plan.afterEnd);
  const [bounty, setBounty] = useState(
    plan.bountyMinor == null ? '' : majorFromMinor(plan.bountyMinor, plan.currency),
  );
  const [bountyOnPayment, setBountyOnPayment] = useState(
    plan.bountyOnPayment == null ? '1' : String(plan.bountyOnPayment),
  );
  const [milestones, setMilestones] = useState<MilestoneRow[]>(() =>
    (plan.milestones ?? []).map((row) =>
      newMilestone(String(row.qualifiedReferrals), majorFromMinor(row.amountMinor, plan.currency)),
    ),
  );
  const [perReferralCap, setPerReferralCap] = useState(
    plan.perReferralCapMinor == null ? '' : majorFromMinor(plan.perReferralCapMinor, plan.currency),
  );
  const [perAffiliateMonthCap, setPerAffiliateMonthCap] = useState(
    plan.perAffiliateMonthCapMinor == null
      ? ''
      : majorFromMinor(plan.perAffiliateMonthCapMinor, plan.currency),
  );
  // TMMPDM-0089: the limits are the adopted card's live controls (the agreement has no card).
  const { limits } = useAdoptedCardLimits(resellerId);

  const [attempt, setAttempt] = useState(0);
  // TMMPDM-0046/0049 (FRM-005): the last Save's messages, less the fields edited since. The summary
  // and — while the field is unedited — the inline message both read this. A preview answer never
  // removes an entry; the next Save replaces the map.
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<{ message: string; stale: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const [cards, setCards] = useState<CustomerRateCardView[] | null>(null);
  const [cardsFailure, setCardsFailure] = useState<unknown>(null);
  const [cardsToken, setCardsToken] = useState(0);
  const [cardId, setCardId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listCustomerRateCards()
      .then((list) => {
        if (cancelled) return;
        // A published version is what the preview resolves. Not a sellability verdict.
        const granted = list.rateCards.filter(
          (card) => card.grants.includes(resellerId) && card.currentVersionId !== null,
        );
        setCards(granted);
        setCardsFailure(null);
        // The adopted card, when this list includes it. A choice already made stays.
        setCardId((current) => {
          if (current != null) return current;
          const adopted = granted.find((card) => card.adoptingResellers.includes(resellerId));
          return (adopted ?? granted[0])?.id ?? null;
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setCards(null);
        setCardsFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, cardsToken]);

  const snapshot = JSON.stringify([
    name,
    mode,
    simpleRate,
    simpleLife,
    simpleMonths,
    rows.map(({ to, rate }) => [to, rate]),
    appliesTo,
    coolOff,
    afterEnd,
    bounty,
    bountyOnPayment,
    milestones.map(({ qualified, amount }) => [qualified, amount]),
    perReferralCap,
    perAffiliateMonthCap,
  ]);
  const [initialSnapshot] = useState(snapshot);
  const guard = useUnsavedChangesGuard(snapshot !== initialSnapshot, plan.name);

  /** An edit drops that field's Save message (summary and inline) and the whole dry run. */
  const touch = (drop: (key: string) => boolean) => {
    setSaveErrors((prev) => withoutKeys(prev, drop));
    preview.edit();
  };
  const touchKey = (key: string) => touch((k) => k === key);
  const touchSchedule = () => touch((k) => k === 'phases' || k === 'mode' || PHASE_KEY.test(k));

  const phaseCount = mode === 'SIMPLE' ? 1 : rows.length;
  const phaseLabel = (index: number, field: string): string => {
    if (mode === 'SIMPLE') {
      if (field === 'rateBp') return t('commissionPlans.form.rate', 'Rate (%)');
      if (field === 'toMonth') return t('commissionPlans.form.months', 'Number of months');
      return t('commissionPlans.form.schedule', 'Schedule');
    }
    const n = index + 1;
    if (field === 'rateBp')
      return t('commissionPlans.form.phaseRate', 'Phase {{n}} rate (%)', { n });
    if (field === 'toMonth') {
      return t('commissionPlans.form.phaseTo', 'Phase {{n}} to month', { n });
    }
    return t('commissionPlans.form.phaseFrom', 'Phase {{n}} from month', { n });
  };

  /** The label of a field the server or the form names; `null` for one the form doesn't show. */
  const labelOf = (key: string): string | null => {
    if (key === 'name') return t('commissionPlans.form.name', 'Name');
    if (key === 'mode') return t('commissionPlans.form.mode', 'Mode');
    if (key === 'phases') return t('commissionPlans.form.schedule', 'Schedule');
    if (key === 'appliesTo') return t('commissionPlans.form.appliesTo', 'Applies to');
    if (key === 'coolOffDays') return t('commissionPlans.form.coolOff', 'Cool-off (days)');
    if (key === 'afterEnd') {
      return t('commissionPlans.form.afterEnd', 'After the affiliate ends');
    }
    if (key === 'bountyMinor') return t('commissionPlans.form.bounty', 'Bounty');
    if (key === 'bountyOnPayment') {
      return t('commissionPlans.form.bountyOnPayment', "Paid on the referral's payment no.");
    }
    if (key === 'milestones') return t('commissionPlans.form.milestones', 'Milestones');
    if (key === 'perReferralCapMinor') {
      return t('commissionPlans.form.perReferralCap', 'Per referral, at most');
    }
    if (key === 'perAffiliateMonthCapMinor') {
      return t('commissionPlans.form.perAffiliateMonthCap', 'Per affiliate per month, at most');
    }
    const milestone = MILESTONE_KEY.exec(key);
    if (milestone) {
      const n = Number(milestone[1]) + 1;
      return milestone[2] === 'amountMinor'
        ? t('commissionPlans.form.milestoneBonus', 'Milestone {{n}} bonus', { n })
        : t('commissionPlans.form.milestoneQualified', 'Milestone {{n}} qualified referrals', {
            n,
          });
    }
    const match = PHASE_KEY.exec(key);
    if (match) {
      const index = Number(match[1]);
      if (index < phaseCount) return phaseLabel(index, match[2] ?? '');
    }
    return null;
  };

  /** Where a message lands. SIMPLE's From (always 1) is the schedule's. */
  const targetOf = (key: string): string => {
    if (mode === 'SIMPLE' && key === 'phases[0].fromMonth') return 'phases';
    return key;
  };

  /** A typed 0 is not "none". Empty is none. */
  const zeroMoney = (value: string): boolean => {
    const trimmed = value.trim();
    return trimmed !== '' && !invalidMoney(trimmed) && parseMajor(trimmed, plan.currency) === 0;
  };
  const leaveEmpty = t('commissionPlans.form.leaveEmpty', 'Leave it empty for none');

  /** Client-side basic ranges only; the limits are the server's. */
  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    const rateMsg = t('commissionPlans.form.rateRange', 'Enter a rate from 0 to 100.');
    if (name.trim() === '') next.name = t('commissionPlans.form.nameRequired', 'Name is required.');
    else if (name.trim().length > NAME_MAX) {
      next.name = t('commissionPlans.form.nameLong', 'Use at most {{max}} characters.', {
        max: NAME_MAX,
      });
    }
    if (mode === 'SIMPLE') {
      if (invalidRate(simpleRate)) next['phases[0].rateBp'] = rateMsg;
      if (!simpleLife && invalidWhole(simpleMonths, 1, Number.MAX_SAFE_INTEGER)) {
        next['phases[0].toMonth'] = t(
          'commissionPlans.form.monthsRange',
          'Enter a whole number of months, 1 or more.',
        );
      }
    } else {
      rows.forEach((row, index) => {
        if (invalidRate(row.rate)) next[`phases[${index}].rateBp`] = rateMsg;
        const last = index === rows.length - 1;
        const from = fromOf(rows, index);
        if (row.to.trim() === '') {
          if (!last) {
            next[`phases[${index}].toMonth`] = t(
              'commissionPlans.form.onlyLastOpen',
              'Only the last phase may be onward.',
            );
          }
        } else if (from !== null && invalidWhole(row.to, from, Number.MAX_SAFE_INTEGER)) {
          next[`phases[${index}].toMonth`] = t(
            'commissionPlans.form.toRange',
            'Enter a month from {{from}} on.',
            { from },
          );
        }
      });
    }
    if (invalidWhole(coolOff, 0, COOL_OFF_MAX)) {
      next.coolOffDays = t(
        'commissionPlans.form.coolOffRange',
        'Enter a whole number from 0 to {{max}}.',
        {
          max: COOL_OFF_MAX,
        },
      );
    }
    if (mode === 'ADVANCED') {
      const amountInvalid = t('commissionPlans.form.amountInvalid', 'Enter an amount.');
      if (bounty.trim() !== '') {
        if (zeroMoney(bounty)) next.bountyMinor = leaveEmpty;
        else if (invalidMoney(bounty)) next.bountyMinor = amountInvalid;
        if (invalidWhole(bountyOnPayment, 1, MAX_BOUNTY_ON_PAYMENT)) {
          next.bountyOnPayment = t(
            'commissionPlans.form.bountyOnPaymentRange',
            'Enter a whole number from 1 to {{max}}.',
            { max: MAX_BOUNTY_ON_PAYMENT },
          );
        }
      }
      milestones.forEach((row, index) => {
        if (invalidWhole(row.qualified, 1, Number.MAX_SAFE_INTEGER)) {
          next[`milestones[${index}].qualifiedReferrals`] = t(
            'commissionPlans.form.qualifiedRange',
            'Enter a whole number, 1 or more.',
          );
        }
        if (row.amount.trim() === '' || invalidMoney(row.amount)) {
          next[`milestones[${index}].amountMinor`] = amountInvalid;
        }
      });
      if (perReferralCap.trim() !== '') {
        if (zeroMoney(perReferralCap)) next.perReferralCapMinor = leaveEmpty;
        else if (invalidMoney(perReferralCap)) next.perReferralCapMinor = amountInvalid;
      }
      if (perAffiliateMonthCap.trim() !== '') {
        if (zeroMoney(perAffiliateMonthCap)) next.perAffiliateMonthCapMinor = leaveEmpty;
        else if (invalidMoney(perAffiliateMonthCap)) {
          next.perAffiliateMonthCapMinor = amountInvalid;
        }
      }
    }
    return next;
  };

  const phasesBody = (): CommissionPhase[] => {
    const rateBp = (value: string) => Math.round(Number(value) * 100);
    if (mode === 'SIMPLE') {
      const phase: CommissionPhase = { fromMonth: 1, rateBp: rateBp(simpleRate) };
      if (!simpleLife) phase.toMonth = Number(simpleMonths);
      return [phase];
    }
    return rows.map((row, index) => {
      const phase: CommissionPhase = {
        fromMonth: fromOf(rows, index) ?? 1,
        rateBp: rateBp(row.rate),
      };
      if (row.to.trim() !== '') phase.toMonth = Number(row.to);
      return phase;
    });
  };

  const bountySet = bounty.trim() !== '';
  const milestonesSet = milestones.length > 0;
  const referralCapSet = perReferralCap.trim() !== '';
  const monthCapSet = perAffiliateMonthCap.trim() !== '';
  const economicsSet = bountySet || milestonesSet || referralCapSet || monthCapSet;

  /**
   * The five ADVANCED components. Empty inputs, and every Simple save, send the server's clear
   * (`0`, `[]`). Simple is not offered while any of them is set, so a Simple body never carries
   * a bounty, a milestone or a cap.
   */
  const economicsBody = (): Pick<
    CommissionPlanWriteRequest,
    | 'bountyMinor'
    | 'bountyOnPayment'
    | 'milestones'
    | 'perReferralCapMinor'
    | 'perAffiliateMonthCapMinor'
  > => {
    if (mode === 'SIMPLE') {
      return {
        bountyMinor: 0,
        milestones: [],
        perReferralCapMinor: 0,
        perAffiliateMonthCapMinor: 0,
      };
    }
    const money = (value: string) => parseMajor(value, plan.currency);
    return {
      bountyMinor: bountySet ? money(bounty) : 0,
      ...(bountySet ? { bountyOnPayment: Number(bountyOnPayment) } : {}),
      milestones: milestones.map((row) => ({
        qualifiedReferrals: Number(row.qualified),
        amountMinor: money(row.amount),
      })),
      perReferralCapMinor: referralCapSet ? money(perReferralCap) : 0,
      perAffiliateMonthCapMinor: monthCapSet ? money(perAffiliateMonthCap) : 0,
    };
  };

  /** The whole plan as a write request. */
  const buildBody = (): CommissionPlanWriteRequest => ({
    name: name.trim(),
    mode,
    appliesTo,
    coolOffDays: Number(coolOff),
    afterEnd,
    phases: phasesBody(),
    ...economicsBody(),
  });

  const clientErrors = validate();
  const clientValid = Object.keys(clientErrors).length === 0;
  const previewBody = (): CommissionPlanPreviewRequest => ({
    ...buildBody(),
    ...(cardId !== null ? { rateCardId: cardId } : {}),
  });
  // The body the check sends. Empty while the inputs can't be sent, or while there is no rate
  // card: a body without `rateCardId` is not a verdict on the plan, so it is never sent.
  const bodyKey = clientValid && cardId !== null ? JSON.stringify(previewBody()) : '';
  const cardsReady = cardId !== null;

  const requestPreview = useCallback(
    async (body: CommissionPlanPreviewRequest) => {
      const answer = await adminApi.previewCommissionPlan(resellerId, body);
      // The dry run's per-field messages are the server's text: the fields stay, the words are ours.
      return { ...answer, fields: catalogFields(answer.fields, t) };
    },
    [resellerId, t],
  );
  const preview = useLivePreview<CommissionPlanPreviewRequest, CommissionPlanPreview>({
    bodyKey,
    ready: cardsReady,
    request: requestPreview,
  });

  const showErrors = (next: Record<string, string>) => {
    setSaveErrors(next);
    if (Object.keys(next).some((key) => labelOf(key) !== null)) setAttempt((n) => n + 1);
  };

  const check = () => {
    // Nothing is sent (client-invalid, or no rate card yet): leave the Save messages as they are.
    if (!clientValid || !cardsReady) return;
    preview.run(previewBody());
  };

  const submit = async () => {
    if (inFlight.current) return;
    preview.cancelPending();
    showErrors(clientErrors);
    setFormError(null);
    if (!clientValid) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      const saved = await adminApi.patchCommissionPlan(resellerId, plan.id, {
        ...buildBody(),
        version: plan.version,
      });
      notify.success(
        t('commissionPlans.toast.saved', '{{name}} saved. Activate it when it’s ready.', {
          name: saved.name,
        }),
      );
      // TMMPDM-0046 (FRM-012): back with `replace`, unless the user has already left.
      guard.leaveAfterSave(affiliateCommissionsTab(resellerId));
    } catch (err) {
      const taken = commissionPlanNameTaken(err);
      if (taken !== null) {
        // FRM-004: the clash lands on Name and in the error summary.
        showErrors({
          name: t(
            'commissionPlans.errors.nameTaken',
            'Another of this reseller\'s plans is called "{{name}}".',
            { name: taken || name.trim() },
          ),
        });
      } else if (isValidationFailed(err)) {
        const fields = validationFields(err, t);
        showErrors(fields);
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (Object.keys(fields).some((key) => labelOf(key) === null)) {
          setFormError({
            message: errorCopy(
              err,
              t,
              t('commissionPlans.errors.save', "Couldn't save the commission plan."),
            ),
            stale: false,
          });
        }
      } else if (err instanceof ApiError && String(err.code) === 'COMMISSION_PLAN_IMMUTABLE') {
        setFormError({
          message: t(
            'commissionPlans.errors.immutable',
            'This plan is no longer a draft. Copy it to change it.',
          ),
          stale: true,
        });
      } else {
        // Reload only for a stale version; any other 409 is its own code with its own copy.
        setFormError({
          message: errorCopy(
            err,
            t,
            t('commissionPlans.errors.save', "Couldn't save the commission plan."),
          ),
          stale: isStaleWrite(err),
        });
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  // The Save's message while the field is unedited since that Save; otherwise the client's, while
  // the inputs can't be checked, or the latest dry run's. No card is not an error.
  const messageFor = (key: string): string | undefined => {
    const saved = Object.entries(saveErrors).find(([k]) => targetOf(k) === key)?.[1];
    if (saved) return saved;
    if (!clientValid) {
      const client = Object.entries(clientErrors).find(([k]) => targetOf(k) === key)?.[1];
      if (client) return client;
    }
    const checked = Object.entries(preview.result?.fields ?? {}).find(
      ([k]) => targetOf(k) === key,
    )?.[1];
    return checked || undefined;
  };

  const switchMode = (next: CommissionPlanMode) => {
    if (next === mode) return;
    if (next === 'ADVANCED') {
      setRows([newRow(simpleLife ? '' : simpleMonths, simpleRate)]);
    } else {
      const only = rows[0];
      if (rows.length !== 1 || !only || economicsSet) return;
      setSimpleRate(only.rate);
      setSimpleLife(only.to.trim() === '');
      if (only.to.trim() !== '') setSimpleMonths(only.to);
    }
    setMode(next);
    touchSchedule();
  };

  const updateRow = (index: number, patch: Partial<PhaseRow>, key: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    touchKey(key);
    // A changed To moves the next row's From.
    if (patch.to !== undefined) touchKey(`phases[${index + 1}].fromMonth`);
  };

  const addRow = () => {
    if (rows.length >= MAX_PHASES) return;
    setRows((prev) => [...prev, newRow()]);
    touchSchedule();
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
    touchSchedule();
  };

  const title = t('commissionPlans.form.title', 'Edit {{name}}', { name: plan.name });
  // The last Save's fields, less those edited since; a Check or a dry run never changes it.
  const summaryKeys = Object.keys(saveErrors).filter((key) => labelOf(key) !== null);
  const summary: ErrorSummaryItem[] = summaryKeys.map((key) => ({
    id: fieldId(targetOf(key)),
    label: labelOf(key) ?? key,
    message: saveErrors[key] ?? '',
  }));

  const cardName = (id: number) => cards?.find((card) => card.id === id)?.name ?? `#${id}`;
  const noCard = cards !== null && cards.length === 0;
  const previewTitle = t('commissionPlans.preview.title', 'Check against the limits');
  const helpFor = (label: string, short: string) => <HelpPopover label={label} short={short} />;

  const radioGroup = (
    key: 'mode' | 'appliesTo' | 'afterEnd',
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { value: string; label: string; helper?: string; disabled?: boolean }[],
    help?: ReactNode,
  ) => {
    const message = messageFor(key);
    return (
      <FormControl component="fieldset" error={Boolean(message)}>
        <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
          <FormLabel component="legend" sx={{ float: 'left' }}>
            {label}
          </FormLabel>
          {help}
        </Stack>
        <RadioGroup
          id={fieldId(key)}
          tabIndex={-1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
            <Box key={option.value}>
              <FormControlLabel
                value={option.value}
                control={<Radio />}
                label={option.label}
                disabled={option.disabled ?? false}
              />
              {option.helper ? (
                <FormHelperText sx={{ mt: -0.75, ml: 4 }}>{option.helper}</FormHelperText>
              ) : null}
            </Box>
          ))}
        </RadioGroup>
        {message ? <FormHelperText>{message}</FormHelperText> : null}
      </FormControl>
    );
  };

  const scheduleMessage = messageFor('phases');
  const scheduleTitle = t('commissionPlans.form.schedule', 'Schedule');

  return (
    <FormPage
      headerless
      title={title}
      onSubmit={() => void submit()}
      busy={submitting}
      submitLabel={t('commissionPlans.actions.saveDraft', 'Save draft')}
      onCancel={() => void navigate(backTo(affiliateCommissionsTab(resellerId)))}
      guard={guard}
      errorSummary={{ items: attempt > 0 ? summary : [], attempt }}
      formError={
        formError ? (
          <Alert
            severity="error"
            action={
              formError.stale ? (
                <Button color="inherit" size="small" onClick={onReload}>
                  {t('common.reload', 'Reload')}
                </Button>
              ) : undefined
            }
          >
            {formError.message}
          </Alert>
        ) : null
      }
      actions={
        <Button onClick={check} disabled={preview.inFlight || submitting}>
          {t('commissionPlans.actions.check', 'Check')}
        </Button>
      }
      aside={
        <Paper
          component="section"
          aria-labelledby="commission-plan-preview-title"
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Typography
            id="commission-plan-preview-title"
            variant="subtitle1"
            component="h2"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            {previewTitle}
          </Typography>
          {cardsFailure !== null ? (
            <Box sx={{ mb: 1 }}>
              <ErrorState
                error={cardsFailure}
                message={t('commissionPlans.preview.cardsError', "Couldn't load rate cards.")}
                onRetry={() => {
                  setCardsFailure(null);
                  setCardsToken((n) => n + 1);
                }}
              />
            </Box>
          ) : null}
          {cards !== null && cards.length > 0 && cardId !== null ? (
            <TextField
              select
              size="small"
              label={t('commissionPlans.preview.card', 'Rate card for the example')}
              value={String(cardId)}
              onChange={(event) => {
                setCardId(Number(event.target.value));
                preview.edit();
              }}
              sx={{ mb: 1.5, minWidth: 240 }}
            >
              {cards.map((card) => (
                <MenuItem key={card.id} value={String(card.id)}>
                  {card.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <PreviewResult
            preview={preview.result}
            error={
              preview.failure === null
                ? null
                : errorCopy(
                    preview.failure.error,
                    t,
                    t('commissionPlans.preview.error', "Couldn't check the plan."),
                  )
            }
            checking={preview.checking}
            cards={cardsFailure !== null ? 'failed' : cards === null ? 'loading' : 'loaded'}
            blocked={!clientValid}
            noCard={noCard}
            unmapped={[
              ...new Set(
                Object.entries(preview.result?.fields ?? {})
                  .filter(([key]) => labelOf(key) === null)
                  .map(([, message]) => message),
              ),
            ]}
            cardName={cardName}
            locale={locale}
          />
        </Paper>
      }
    >
      {fixedFrom ? (
        <Alert severity="info">
          {t(
            'commissionPlans.form.fixedFrom',
            'Copied from {{name}}. Bring it within the limits, activate it, then move affiliates onto it.',
            { name: fixedFrom },
          )}
        </Alert>
      ) : null}
      <Alert severity="info" role="note">
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>
            {t(
              'commissionPlans.form.warnMargin',
              "A recurring rate never pays more than the reseller's margin on that line. Priced at our wholesale, recurring commission is 0.",
            )}
          </li>
          <li>
            {t(
              'commissionPlans.form.warnAnnual',
              'Annual subscriptions are commissioned at the phase of the payment date, on the whole annual amount.',
            )}
          </li>
          {bountySet || milestonesSet ? (
            <li>
              {t(
                'commissionPlans.form.warnNotLimited',
                "Bounties and milestones aren't limited by margin. They're charged in the reseller's monthly settlement, and a negative month is charged to the reseller's card.",
              )}
            </li>
          ) : null}
        </Box>
      </Alert>

      <FormSection title={t('commissionPlans.form.section.plan', 'Plan')}>
        <FormField width={NAME_WIDTH}>
          <TextField
            id={fieldId('name')}
            label={t('commissionPlans.form.name', 'Name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              touchKey('name');
            }}
            error={Boolean(messageFor('name'))}
            helperText={messageFor('name')}
            required
            fullWidth
          />
        </FormField>
      </FormSection>

      <FormSection
        title={scheduleTitle}
        help={helpFor(
          scheduleTitle,
          t(
            'commissionPlans.help.schedule',
            "Month 1 is the referral's first paid month; trials don't count.",
          ),
        )}
      >
        {radioGroup(
          'mode',
          t('commissionPlans.form.mode', 'Mode'),
          mode,
          (value) => switchMode(value as CommissionPlanMode),
          [
            {
              value: 'SIMPLE',
              label: t('commissionPlans.mode.SIMPLE', 'Simple'),
              ...(mode === 'ADVANCED' && (rows.length > 1 || economicsSet)
                ? {
                    disabled: true,
                    helper: [
                      rows.length > 1
                        ? t('commissionPlans.form.simpleBlocked', 'Remove the extra phases first')
                        : null,
                      economicsSet
                        ? t(
                            'commissionPlans.form.simpleBlockedEconomics',
                            'Remove the bounty, milestones and caps first',
                          )
                        : null,
                    ]
                      .filter((line) => line !== null)
                      .join(' '),
                  }
                : {}),
            },
            { value: 'ADVANCED', label: t('commissionPlans.mode.ADVANCED', 'Advanced') },
          ],
        )}
        <Box id={fieldId('phases')} tabIndex={-1}>
          {mode === 'SIMPLE' ? (
            <SimpleSchedule
              rate={simpleRate}
              life={simpleLife}
              months={simpleMonths}
              rateError={messageFor('phases[0].rateBp')}
              monthsError={messageFor('phases[0].toMonth')}
              onRate={(value) => {
                setSimpleRate(value);
                touchKey('phases[0].rateBp');
              }}
              onLife={(value) => {
                setSimpleLife(value);
                touchKey('phases[0].toMonth');
              }}
              onMonths={(value) => {
                setSimpleMonths(value);
                touchKey('phases[0].toMonth');
              }}
            />
          ) : (
            <PhasesTable
              rows={rows}
              messageFor={messageFor}
              onChange={updateRow}
              onAdd={addRow}
              onRemove={removeRow}
            />
          )}
          {scheduleMessage ? (
            <Typography
              variant="caption"
              color="error"
              role="alert"
              sx={{ display: 'block', mt: 1 }}
            >
              {scheduleMessage}
            </Typography>
          ) : null}
        </Box>
      </FormSection>

      {mode === 'ADVANCED' ? (
        <AdvancedEconomics
          bounty={bounty}
          bountyOnPayment={bountyOnPayment}
          milestones={milestones}
          perReferralCap={perReferralCap}
          perAffiliateMonthCap={perAffiliateMonthCap}
          messageFor={messageFor}
          bountyLimit={
            limits
              ? t('commissionPlans.form.upTo', 'Up to {{amount}}.', {
                  amount: formatMoney(limits.bountyMaxMinor, limits.currency, locale),
                })
              : undefined
          }
          milestoneLimit={
            limits
              ? t('commissionPlans.form.eachUpTo', 'Each up to {{amount}}.', {
                  amount: formatMoney(limits.milestoneMaxMinor, limits.currency, locale),
                })
              : undefined
          }
          onBounty={(value) => {
            setBounty(value);
            touchKey('bountyMinor');
          }}
          onBountyOnPayment={(value) => {
            setBountyOnPayment(value);
            touchKey('bountyOnPayment');
          }}
          onMilestone={(index, patch, key) => {
            setMilestones((prev) =>
              prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
            );
            touchKey(key);
          }}
          onAddMilestone={() => {
            if (milestones.length >= MAX_MILESTONES) return;
            setMilestones((prev) => [...prev, newMilestone()]);
            touch((k) => k === 'milestones' || k.startsWith('milestones['));
          }}
          onRemoveMilestone={(index) => {
            setMilestones((prev) => prev.filter((_, i) => i !== index));
            touch((k) => k === 'milestones' || k.startsWith('milestones['));
          }}
          onPerReferralCap={(value) => {
            setPerReferralCap(value);
            touchKey('perReferralCapMinor');
          }}
          onPerAffiliateMonthCap={(value) => {
            setPerAffiliateMonthCap(value);
            touchKey('perAffiliateMonthCapMinor');
          }}
        />
      ) : null}

      <FormSection title={t('commissionPlans.form.section.terms', 'Terms')}>
        {radioGroup(
          'appliesTo',
          t('commissionPlans.form.appliesTo', 'Applies to'),
          appliesTo,
          (value) => {
            setAppliesTo(value as CommissionAppliesTo);
            touchKey('appliesTo');
          },
          [
            {
              value: 'BASE',
              label: t('commissionPlans.appliesTo.BASE', 'Base price'),
              helper: t('commissionPlans.form.appliesToBase', 'The subscription price only.'),
            },
            {
              value: 'ALL',
              label: t('commissionPlans.appliesTo.ALL', 'All lines'),
              helper: t(
                'commissionPlans.form.appliesToAll',
                'Base, seats, connected accounts and AI (never tax).',
              ),
            },
          ],
        )}
        <FormField width={VALUE_WIDTH}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            <TextField
              id={fieldId('coolOffDays')}
              label={t('commissionPlans.form.coolOff', 'Cool-off (days)')}
              value={coolOff}
              onChange={(event) => {
                setCoolOff(event.target.value);
                touchKey('coolOffDays');
              }}
              error={Boolean(messageFor('coolOffDays'))}
              helperText={messageFor('coolOffDays')}
              required
              fullWidth
            />
            <Box sx={{ pt: 1.25 }}>
              {helpFor(
                t('commissionPlans.form.coolOff', 'Cool-off (days)'),
                t(
                  'commissionPlans.help.coolOff',
                  'Commission is held this long after each payment and voided if the payment is refunded in that time. Longer is safer; shorter is more attractive.',
                ),
              )}
            </Box>
          </Stack>
        </FormField>
        {radioGroup(
          'afterEnd',
          t('commissionPlans.form.afterEnd', 'After the affiliate ends'),
          afterEnd,
          (value) => {
            setAfterEnd(value as CommissionAfterEnd);
            touchKey('afterEnd');
          },
          [
            {
              value: 'STOP',
              label: t('commissionPlans.afterEnd.STOP', 'Stop'),
              helper: t(
                'commissionPlans.form.afterEndStop',
                "Nothing more is earned after the affiliate's end date.",
              ),
            },
            {
              value: 'HONOR',
              label: t('commissionPlans.afterEnd.HONOR', 'Honor'),
              helper: t('commissionPlans.form.afterEndHonor', 'Existing referrals keep earning.'),
            },
          ],
          helpFor(
            t('commissionPlans.form.afterEnd', 'After the affiliate ends'),
            t(
              'commissionPlans.help.afterEnd',
              'What happens to commission on existing referrals once the affiliate ends.',
            ),
          ),
        )}
      </FormSection>
    </FormPage>
  );
}

function AdvancedEconomics({
  bounty,
  bountyOnPayment,
  milestones,
  perReferralCap,
  perAffiliateMonthCap,
  messageFor,
  bountyLimit,
  milestoneLimit,
  onBounty,
  onBountyOnPayment,
  onMilestone,
  onAddMilestone,
  onRemoveMilestone,
  onPerReferralCap,
  onPerAffiliateMonthCap,
}: {
  bounty: string;
  bountyOnPayment: string;
  milestones: MilestoneRow[];
  perReferralCap: string;
  perAffiliateMonthCap: string;
  messageFor: (key: string) => string | undefined;
  bountyLimit: string | undefined;
  milestoneLimit: string | undefined;
  onBounty: (value: string) => void;
  onBountyOnPayment: (value: string) => void;
  onMilestone: (index: number, patch: Partial<MilestoneRow>, key: string) => void;
  onAddMilestone: () => void;
  onRemoveMilestone: (index: number) => void;
  onPerReferralCap: (value: string) => void;
  onPerAffiliateMonthCap: (value: string) => void;
}) {
  const { t } = useCopy();
  const bountyLabel = t('commissionPlans.form.bounty', 'Bounty');
  const paymentLabel = t(
    'commissionPlans.form.bountyOnPayment',
    "Paid on the referral's payment no.",
  );
  const milestonesLabel = t('commissionPlans.form.milestones', 'Milestones');
  const referralLabel = t('commissionPlans.form.perReferralCap', 'Per referral, at most');
  const monthLabel = t(
    'commissionPlans.form.perAffiliateMonthCap',
    'Per affiliate per month, at most',
  );
  const bountyMessage = messageFor('bountyMinor');
  const paymentMessage = messageFor('bountyOnPayment');
  const milestonesMessage = messageFor('milestones');
  const referralMessage = messageFor('perReferralCapMinor');
  const monthMessage = messageFor('perAffiliateMonthCapMinor');
  const noCap = t('commissionPlans.form.noCap', 'No cap');
  return (
    <FormSection title={t('commissionPlans.form.section.advanced', 'Bounty, milestones and caps')}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
        useFlexGap
      >
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('bountyMinor')}
            label={bountyLabel}
            value={bounty}
            onChange={(event) => onBounty(event.target.value)}
            error={Boolean(bountyMessage)}
            helperText={bountyMessage ?? bountyLimit}
            fullWidth
          />
        </FormField>
        <Box sx={{ pt: 1 }}>
          <HelpPopover
            label={bountyLabel}
            short={t(
              'commissionPlans.form.bountyHelp',
              'A one-time amount, paid when the referral makes that many base payments, after the cool-off. Voided if a payment is refunded before then.',
            )}
          />
        </Box>
        {bounty.trim() !== '' ? (
          <FormField width={VALUE_WIDTH}>
            <TextField
              id={fieldId('bountyOnPayment')}
              label={paymentLabel}
              value={bountyOnPayment}
              onChange={(event) => onBountyOnPayment(event.target.value)}
              error={Boolean(paymentMessage)}
              helperText={paymentMessage}
              fullWidth
            />
          </FormField>
        ) : null}
      </Stack>
      <Box id={fieldId('milestones')} tabIndex={-1}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" component="h3">
            {milestonesLabel}
          </Typography>
          <HelpPopover
            label={milestonesLabel}
            short={t(
              'commissionPlans.form.milestonesHelp',
              "Paid once each when the affiliate's count of qualified referrals (paid, past cool-off, still subscribed) first reaches the number, at the reseller's month-end.",
            )}
          />
        </Stack>
        <MilestonesTable
          rows={milestones}
          messageFor={messageFor}
          onChange={onMilestone}
          onAdd={onAddMilestone}
          onRemove={onRemoveMilestone}
        />
        {milestoneLimit ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {milestoneLimit}
          </Typography>
        ) : null}
        {milestonesMessage ? (
          <Typography variant="caption" color="error" role="alert" sx={{ display: 'block' }}>
            {milestonesMessage}
          </Typography>
        ) : null}
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormField width={VALUE_WIDTH}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            <TextField
              id={fieldId('perReferralCapMinor')}
              label={referralLabel}
              value={perReferralCap}
              placeholder={noCap}
              onChange={(event) => onPerReferralCap(event.target.value)}
              error={Boolean(referralMessage)}
              helperText={
                referralMessage ??
                t(
                  'commissionPlans.form.perReferralCapHelp',
                  'The most one referral can ever earn the affiliate.',
                )
              }
              fullWidth
            />
          </Stack>
        </FormField>
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('perAffiliateMonthCapMinor')}
            label={monthLabel}
            value={perAffiliateMonthCap}
            placeholder={noCap}
            onChange={(event) => onPerAffiliateMonthCap(event.target.value)}
            error={Boolean(monthMessage)}
            helperText={
              monthMessage ??
              t(
                'commissionPlans.form.perAffiliateMonthCapHelp',
                'The most one affiliate can earn from you in a month.',
              )
            }
            fullWidth
          />
        </FormField>
      </Stack>
    </FormSection>
  );
}

function MilestonesTable({
  rows,
  messageFor,
  onChange,
  onAdd,
  onRemove,
}: {
  rows: MilestoneRow[];
  messageFor: (key: string) => string | undefined;
  onChange: (index: number, patch: Partial<MilestoneRow>, key: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useCopy();
  const tableLabel = t('commissionPlans.form.milestones', 'Milestones');
  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={tableLabel}>
          <TableHead>
            <TableRow>
              <TableCell>
                {t('commissionPlans.detail.qualifiedReferrals', 'Qualified referrals')}
              </TableCell>
              <TableCell>{t('commissionPlans.detail.bonus', 'Bonus')}</TableCell>
              <TableCell>
                <Box component="span" sx={visuallyHidden}>
                  {t('commissionPlans.form.rowActions', 'Remove')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const n = index + 1;
              const qualifiedKey = `milestones[${index}].qualifiedReferrals`;
              const amountKey = `milestones[${index}].amountMinor`;
              const qualifiedError = messageFor(qualifiedKey);
              const amountError = messageFor(amountKey);
              return (
                <TableRow key={row.key} sx={{ verticalAlign: 'top' }}>
                  <TableCell sx={{ width: '40%' }}>
                    <TextField
                      id={fieldId(qualifiedKey)}
                      size="small"
                      value={row.qualified}
                      onChange={(event) =>
                        onChange(index, { qualified: event.target.value }, qualifiedKey)
                      }
                      error={Boolean(qualifiedError)}
                      helperText={qualifiedError}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t(
                            'commissionPlans.form.milestoneQualified',
                            'Milestone {{n}} qualified referrals',
                            { n },
                          ),
                        },
                      }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ width: '40%' }}>
                    <TextField
                      id={fieldId(amountKey)}
                      size="small"
                      value={row.amount}
                      onChange={(event) =>
                        onChange(index, { amount: event.target.value }, amountKey)
                      }
                      error={Boolean(amountError)}
                      helperText={amountError}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t(
                            'commissionPlans.form.milestoneBonus',
                            'Milestone {{n}} bonus',
                            { n },
                          ),
                        },
                      }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label={t(
                        'commissionPlans.form.removeMilestone',
                        'Remove milestone {{n}}',
                        {
                          n,
                        },
                      )}
                      onClick={() => onRemove(index)}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button onClick={onAdd} disabled={rows.length >= MAX_MILESTONES} sx={{ mt: 1 }}>
        {t('commissionPlans.form.addMilestone', 'Add milestone')}
      </Button>
    </Box>
  );
}

function SimpleSchedule({
  rate,
  life,
  months,
  rateError,
  monthsError,
  onRate,
  onLife,
  onMonths,
}: {
  rate: string;
  life: boolean;
  months: string;
  rateError: string | undefined;
  monthsError: string | undefined;
  onRate: (value: string) => void;
  onLife: (value: boolean) => void;
  onMonths: (value: string) => void;
}) {
  const { t } = useCopy();
  const durationLabel = t('commissionPlans.form.duration', 'Duration');
  return (
    <Stack spacing={2}>
      <FormField width={VALUE_WIDTH}>
        <TextField
          id={fieldId('phases[0].rateBp')}
          label={t('commissionPlans.form.rate', 'Rate (%)')}
          value={rate}
          onChange={(event) => onRate(event.target.value)}
          error={Boolean(rateError)}
          helperText={rateError}
          required
          fullWidth
        />
      </FormField>
      <FormControl component="fieldset">
        <FormLabel component="legend">{durationLabel}</FormLabel>
        <RadioGroup
          value={life ? 'life' : 'months'}
          onChange={(event) => onLife(event.target.value === 'life')}
        >
          <FormControlLabel
            value="months"
            control={<Radio />}
            label={t('commissionPlans.form.forMonths', 'For a number of months')}
          />
          <FormControlLabel
            value="life"
            control={<Radio />}
            label={t('commissionPlans.form.forLife', 'For life')}
          />
        </RadioGroup>
      </FormControl>
      {!life ? (
        <FormField width={VALUE_WIDTH}>
          <TextField
            id={fieldId('phases[0].toMonth')}
            label={t('commissionPlans.form.months', 'Number of months')}
            value={months}
            onChange={(event) => onMonths(event.target.value)}
            error={Boolean(monthsError)}
            helperText={monthsError}
            required
            fullWidth
          />
        </FormField>
      ) : null}
    </Stack>
  );
}

function PhasesTable({
  rows,
  messageFor,
  onChange,
  onAdd,
  onRemove,
}: {
  rows: PhaseRow[];
  messageFor: (key: string) => string | undefined;
  onChange: (index: number, patch: Partial<PhaseRow>, key: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useCopy();
  const tableLabel = t('commissionPlans.form.phases', 'Phases');
  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={tableLabel}>
          <TableHead>
            <TableRow>
              <TableCell>{t('commissionPlans.detail.fromMonth', 'From month')}</TableCell>
              <TableCell>{t('commissionPlans.detail.toMonth', 'To month')}</TableCell>
              <TableCell>{t('commissionPlans.form.rate', 'Rate (%)')}</TableCell>
              <TableCell>
                <Box component="span" sx={visuallyHidden}>
                  {t('commissionPlans.form.rowActions', 'Remove')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const n = index + 1;
              const from = fromOf(rows, index);
              const last = index === rows.length - 1;
              const fromKey = `phases[${index}].fromMonth`;
              const toKey = `phases[${index}].toMonth`;
              const rateKey = `phases[${index}].rateBp`;
              const fromError = messageFor(fromKey);
              const toError = messageFor(toKey);
              const rateError = messageFor(rateKey);
              return (
                <TableRow key={row.key} sx={{ verticalAlign: 'top' }}>
                  <TableCell sx={{ width: '26%' }}>
                    <TextField
                      id={fieldId(fromKey)}
                      size="small"
                      value={from === null ? '—' : String(from)}
                      error={Boolean(fromError)}
                      helperText={fromError}
                      slotProps={{
                        htmlInput: {
                          readOnly: true,
                          'aria-label': t(
                            'commissionPlans.form.phaseFrom',
                            'Phase {{n}} from month',
                            {
                              n,
                            },
                          ),
                        },
                      }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ width: '30%' }}>
                    <TextField
                      id={fieldId(toKey)}
                      size="small"
                      value={row.to}
                      placeholder={last ? t('commissionPlans.detail.onward', 'Onward') : undefined}
                      onChange={(event) => onChange(index, { to: event.target.value }, toKey)}
                      error={Boolean(toError)}
                      helperText={toError}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('commissionPlans.form.phaseTo', 'Phase {{n}} to month', {
                            n,
                          }),
                        },
                      }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ width: '30%' }}>
                    <TextField
                      id={fieldId(rateKey)}
                      size="small"
                      value={row.rate}
                      onChange={(event) => onChange(index, { rate: event.target.value }, rateKey)}
                      error={Boolean(rateError)}
                      helperText={rateError}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t(
                            'commissionPlans.form.phaseRate',
                            'Phase {{n}} rate (%)',
                            {
                              n,
                            },
                          ),
                        },
                      }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label={t('commissionPlans.form.removePhase', 'Remove phase {{n}}', {
                        n,
                      })}
                      onClick={() => onRemove(index)}
                      disabled={rows.length <= 1}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button onClick={onAdd} disabled={rows.length >= MAX_PHASES} sx={{ mt: 1 }}>
        {t('commissionPlans.form.addPhase', 'Add phase')}
      </Button>
    </Box>
  );
}

/** SubmitButton's visually hidden text (px strings: a bare 1 is 100% in `sx`). */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

function PreviewResult({
  preview,
  error,
  checking,
  cards,
  blocked,
  noCard,
  unmapped,
  cardName,
  locale,
}: {
  preview: CommissionPlanPreview | null;
  error: string | null;
  checking: boolean;
  /** The reseller's rate cards: nothing is checked until they have loaded. */
  cards: 'loading' | 'failed' | 'loaded';
  /** The inputs fail the client's basic checks: nothing is checked, and no verdict is shown. */
  blocked: boolean;
  noCard: boolean;
  unmapped: string[];
  cardName: (id: number) => string;
  locale: string;
}) {
  const { t } = useCopy();
  const noCardText = t(
    'commissionPlans.preview.noCard',
    'Add a rate card for this reseller to see an example.',
  );
  // The failed load's alert (with Try again) sits above: no verdict without the cards.
  if (cards === 'failed') return <Box aria-live="polite" />;
  if (cards === 'loading' || blocked) {
    return (
      <Box aria-live="polite">
        <Typography variant="body2" color="text.secondary">
          {cards === 'loading'
            ? t('commissionPlans.preview.loadingCards', 'Loading rate cards…')
            : t(
                'commissionPlans.preview.fixFields',
                'Fix the highlighted fields to check this plan.',
              )}
        </Typography>
      </Box>
    );
  }
  // The verdict is the server's `valid`, as is. No card never produces an answer (nothing is
  // sent without `rateCardId`); the empty state says how to get an example.
  return (
    <Box aria-live="polite">
      {checking ? (
        <Typography variant="body2" color="text.secondary">
          {t('commissionPlans.preview.checking', 'Checking…')}
        </Typography>
      ) : null}
      {!checking && error ? <Alert severity="error">{error}</Alert> : null}
      {!checking && !error && !preview ? (
        <Typography variant="body2" color="text.secondary">
          {noCard
            ? noCardText
            : t(
                'commissionPlans.preview.empty',
                'Change a field or choose Check to see whether the plan is within the limits and what it pays.',
              )}
        </Typography>
      ) : null}
      {!checking && preview ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {preview.valid ? (
            <Alert severity="success">
              {t('commissionPlans.preview.valid', 'This plan is within the limits.')}
            </Alert>
          ) : (
            <Alert severity="warning">
              {t(
                'commissionPlans.preview.invalid',
                'This plan breaks a limit or a schedule rule. Each field that does says why.',
              )}
              {unmapped.length > 0 ? ` ${unmapped.join(' ')}` : null}
            </Alert>
          )}
          {preview.example ? (
            <ExampleTable example={preview.example} cardName={cardName} locale={locale} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('commissionPlans.preview.noExample', 'Fix the schedule to see an example.')}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

function ExampleTable({
  example,
  cardName,
  locale,
}: {
  example: CommissionPlanPreviewExample;
  cardName: (id: number) => string;
  locale: string;
}) {
  const { t } = useCopy();
  const label = t('commissionPlans.preview.exampleAria', 'Example by month');
  const money = (minor: number) => (
    <Amount minor={minor} currency={example.currency} locale={locale} />
  );
  return (
    <Box>
      <Typography variant="subtitle2" component="h3">
        {t(
          'commissionPlans.preview.exampleTitle',
          'One subscriber at {{amount}} a month on {{card}}',
          {
            amount: formatMoney(example.retailMinor, example.currency, locale),
            card: cardName(example.rateCardId),
          },
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t(
          'commissionPlans.preview.exampleCaption',
          'Paid months 1–24, the first collected today. Payable after a {{days}}-day cool-off.',
          { days: example.coolOffDays },
        )}
      </Typography>
      {/* Scrolls sideways inside the panel: width 0 / min-width 100% keeps the table's own width
          out of the page's, so the page never scrolls sideways (the form keeps its column). */}
      <TableContainer
        tabIndex={0}
        role="region"
        aria-label={label}
        sx={{ width: 0, minWidth: '100%' }}
      >
        <Table size="small" aria-label={label}>
          <TableHead>
            <TableRow>
              <TableCell>{t('commissionPlans.preview.col.month', 'Month')}</TableCell>
              <TableCell>{t('commissionPlans.preview.col.collected', 'Collected')}</TableCell>
              <TableCell align="right">{t('commissionPlans.preview.col.rate', 'Rate')}</TableCell>
              <TableCell align="right">
                {t('commissionPlans.preview.col.commission', 'Commission')}
              </TableCell>
              <TableCell align="right">
                {t('commissionPlans.preview.col.marginBefore', 'Reseller margin before')}
              </TableCell>
              <TableCell align="right">
                {t('commissionPlans.preview.col.marginAfter', 'Reseller margin after')}
              </TableCell>
              <TableCell align="right">
                {t('commissionPlans.preview.col.bounty', 'Bounty')}
              </TableCell>
              <TableCell>{t('commissionPlans.preview.col.capped', 'Capped')}</TableCell>
              <TableCell>{t('commissionPlans.preview.col.payableOn', 'Payable on')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {example.months.map((row) => (
              <TableRow key={row.month}>
                <TableCell component="th" scope="row">
                  {row.month}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatDate(row.collectedOn, locale)}
                </TableCell>
                <TableCell align="right">{formatPercentBp(row.rateBp, locale)}</TableCell>
                <TableCell align="right">{money(row.commissionMinor)}</TableCell>
                <TableCell align="right">{money(row.marginBeforeMinor)}</TableCell>
                <TableCell align="right">{money(row.marginAfterMinor)}</TableCell>
                <TableCell align="right">
                  {row.bountyMinor != null ? money(row.bountyMinor) : '—'}
                </TableCell>
                <TableCell>
                  {row.capped ? t('commissionPlans.preview.capped', 'Capped') : '—'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatDate(row.payableOn, locale)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
